import { useState, useEffect, useCallback, useRef } from 'react';
import { scaleAgentService } from '../services/scaleAgentService';
import { pontBasculeService } from '../services/pontBasculeService';
import {
  ScaleAgentConfigType,
  WeightDataType,
  ScaleAgentStatusType,
  PontBasculeType,
  ScaleCommandType
} from '../types/scaleAgent';

/**
 * Hook React pour la gestion du Scale Agent
 * Gère la connexion, la capture de poids et les commandes
 */
export const useScaleAgent = (siteId?: string, pontBasculeId?: string) => {
  
  // États
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentWeight, setCurrentWeight] = useState<WeightDataType | null>(null);
  const [status, setStatus] = useState<ScaleAgentStatusType>({
    connected: false,
    reconnectAttempts: 0,
    lastError: undefined,
    lastWeight: undefined,
    lastConnectedAt: undefined,
    latency: undefined
  });
  const [error, setError] = useState<string | null>(null);
  const [pontsBascule, setPontsBascule] = useState<PontBasculeType[]>([]);
  const [selectedPontBascule, setSelectedPontBascule] = useState<PontBasculeType | null>(null);
  const [loading, setLoading] = useState(false);

  // Refs
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const weightUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const statusRef = useRef<ScaleAgentStatusType | null>(null);

  // Initialisation
  useEffect(() => {
    initializeScaleAgent();
    
    // Nettoyage à la destruction
    return () => {
      cleanup();
    };
  }, [siteId]);

  // Initialiser le Scale Agent
  const initializeScaleAgent = useCallback(async () => {
    try {
      setLoading(true);
      
      // Charger les ponts bascule du site ou tous les ponts disponibles
      if (siteId) {
        // Si un site est spécifié, charger les ponts de ce site
        const ponts = await pontBasculeService.getPontsBasculeBySite(siteId);
        setPontsBascule(ponts);
        
        // Sélectionner le pont bascule par défaut ou spécifié
        if (pontBasculeId) {
          const selected = ponts.find(p => p.id === pontBasculeId);
          setSelectedPontBascule(selected || null);
        } else if (ponts.length > 0) {
          setSelectedPontBascule(ponts[0]);
        }
      } else {
        // Si aucun site n'est spécifié, charger tous les ponts bascule disponibles
        const allPonts = await pontBasculeService.getAllPontsBascule();
        setPontsBascule(allPonts);
        
        // Sélectionner le pont bascule par défaut ou le premier disponible
        if (pontBasculeId) {
          const selected = allPonts.find(p => p.id === pontBasculeId);
          setSelectedPontBascule(selected || null);
        } else if (allPonts.length > 0) {
          // Priorité au pont par défaut, sinon le premier
          const defaultPont = allPonts.find(p => p.is_default);
          setSelectedPontBascule(defaultPont || allPonts[0]);
        }
      }
      
      // Configurer les écouteurs d'événements
      setupEventListeners();
      
    } catch (error) {
      console.error('Erreur initialisation Scale Agent:', error);
      setError('Erreur lors de l\'initialisation');
    } finally {
      setLoading(false);
    }
  }, [siteId, pontBasculeId]);

  // Configurer les écouteurs d'événements
  const setupEventListeners = useCallback(() => {
    // Événement de connexion
    scaleAgentService.on('connected', () => {
      setIsConnected(true);
      setIsConnecting(false);
      setError(null);
    });

    // Événement de déconnexion
    scaleAgentService.on('disconnected', () => {
      setIsConnected(false);
      setError('Connexion perdue');
    });

    // Événement d'erreur de connexion
    scaleAgentService.on('connection_error', (error: Error) => {
      setIsConnecting(false);
      setError(`Erreur de connexion: ${error.message}`);
    });

    // Événement de changement de statut - utiliser statusRef pour éviter les re-renders inutiles
    scaleAgentService.on('status_changed', (newStatus: ScaleAgentStatusType) => {
      // Ne mettre à jour que si le statut a vraiment changé
      if (!statusRef.current || 
          statusRef.current.connected !== newStatus.connected ||
          statusRef.current.reconnectAttempts !== newStatus.reconnectAttempts ||
          statusRef.current.lastError !== newStatus.lastError) {
        statusRef.current = newStatus;
        setStatus(newStatus);
        setIsConnected(newStatus.connected);
      }
    });

    // Événement de données de poids
    scaleAgentService.on('weight_data', (weightData: WeightDataType) => { 
      setCurrentWeight(weightData);
      setError(null);
    });

    // Événement d'erreur
    scaleAgentService.on('error', (error: Error) => {
      setError(error.message);
    });
  }, []);

  // Se connecter au Scale Agent
  const connect = useCallback(async (config?: ScaleAgentConfigType) => {
    try {
      setIsConnecting(true);
      setError(null);

      const configToUse = config || selectedPontBascule?.scaleAgentConfig;
      if (!configToUse) {
        throw new Error('Aucune configuration disponible');
      }

      // Timeout de connexion
      connectionTimeoutRef.current = setTimeout(() => {
        setIsConnecting(false);
        setError('Timeout de connexion');
      }, 15000);

      await scaleAgentService.connect(configToUse);
      
      // Démarrer la mise à jour automatique du poids
      startWeightUpdateInterval();
      
    } catch (error) {
      setIsConnecting(false);
      setError(error instanceof Error ? error.message : 'Erreur de connexion');
      console.error('Erreur connexion Scale Agent:', error);
    }
  }, [selectedPontBascule]);

  // Se déconnecter du Scale Agent
  const disconnect = useCallback(() => {
    scaleAgentService.disconnect();
    setIsConnected(false);
    setIsConnecting(false);
    setCurrentWeight(null);
    setError(null);
    
    // Arrêter la mise à jour automatique
    stopWeightUpdateInterval();
    
    // Nettoyer le timeout de connexion
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  // Démarrer la mise à jour automatique du poids
  const startWeightUpdateInterval = useCallback(() => {
    if (weightUpdateIntervalRef.current) {
      clearInterval(weightUpdateIntervalRef.current);
    }

    // Ne pas créer d'interval inutile - le poids est mis à jour via les événements WebSocket
    // L'interval était vide et causait des re-renders inutiles
    // weightUpdateIntervalRef.current = null;
  }, []);

  // Arrêter la mise à jour automatique du poids
  const stopWeightUpdateInterval = useCallback(() => {
    if (weightUpdateIntervalRef.current) {
      clearInterval(weightUpdateIntervalRef.current);
      weightUpdateIntervalRef.current = null;
    }
  }, []);

  // Envoyer une commande au Scale Agent
  const sendCommand = useCallback(async (command: ScaleCommandType) => {
    if (!isConnected) {
      throw new Error('Scale Agent non connecté');
    }

    try {
      setError(null);
      const result = await scaleAgentService.sendCommand(command);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur de commande';
      setError(errorMessage);
      throw error;
    }
  }, [isConnected]);

  // Rafraîchir le poids
  const refreshWeight = useCallback(async () => {
    try {
      const weightData = await sendCommand({ action: 'refresh' });
      return weightData;
    } catch (error) {
      console.error('Erreur rafraîchissement poids:', error);
      throw error;
    }
  }, [sendCommand]);

  // Effectuer une tare
  const tare = useCallback(async () => {
    try {
      await sendCommand({ action: 'tare' });
    } catch (error) {
      console.error('Erreur tare:', error);
      throw error;
    }
  }, [sendCommand]);

  // Calibrer la balance
  const calibrate = useCallback(async () => {
    try {
      await sendCommand({ action: 'calibrate' });
    } catch (error) {
      console.error('Erreur calibration:', error);
      throw error;
    }
  }, [sendCommand]);

  // Changer de pont bascule
  const changePontBascule = useCallback(async (pontBasculeId: string) => {
    try {
      setLoading(true);
      
      // Déconnecter du pont bascule actuel
      if (isConnected) {
        disconnect();
      }

      // Sélectionner le nouveau pont bascule
      const newPontBascule = pontsBascule.find(p => p.id === pontBasculeId);
      if (!newPontBascule) {
        throw new Error('Pont bascule non trouvé');
      }

      setSelectedPontBascule(newPontBascule);

      // Se connecter au nouveau pont bascule
      await connect(newPontBascule.scaleAgentConfig);
      
    } catch (error) {
      console.error('Erreur changement pont bascule:', error);
      setError(error instanceof Error ? error.message : 'Erreur de changement');
    } finally {
      setLoading(false);
    }
  }, [pontsBascule, isConnected, disconnect, connect]);

  // Nettoyer les ressources
  const cleanup = useCallback(() => {
    disconnect();
    
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
    }
    
    if (weightUpdateIntervalRef.current) {
      clearInterval(weightUpdateIntervalRef.current);
    }
    
    scaleAgentService.removeAllListeners();
  }, [disconnect]);

  // Vérifier la stabilité du poids
  const isWeightStable = useCallback(() => {
    return currentWeight?.stable || false;
  }, [currentWeight]);

  // Obtenir le poids formaté
  const getFormattedWeight = useCallback(() => {
    if (!currentWeight || (currentWeight.weight === undefined && currentWeight.gross_weight === undefined)) {
      return '--';
    }
    
    const precision = selectedPontBascule?.scaleAgentConfig?.scale?.precision || 2;
    const unit = currentWeight.unit || 'kg';
    // Utiliser weight si disponible, sinon gross_weight
    const weightValue = currentWeight.weight !== undefined ? currentWeight.weight : currentWeight.gross_weight;
    return `${weightValue.toFixed(precision)} ${unit}`;
  }, [currentWeight, selectedPontBascule]);

  // Obtenir le statut de connexion
  const getConnectionStatus = useCallback(() => {
    if (isConnecting) return 'connecting';
    if (isConnected) return 'connected';
    if (error) return 'error';
    return 'disconnected';
  }, [isConnecting, isConnected, error]);


  return {
    // États
    isConnected,
    isConnecting,
    currentWeight,
    status,
    error,
    pontsBascule,
    selectedPontBascule,
    loading,
    
    // Actions
    connect,
    disconnect,
    sendCommand,
    refreshWeight,
    tare,
    calibrate,
    changePontBascule,
    
    // Utilitaires
    isWeightStable,
    getFormattedWeight,
    getConnectionStatus,
    
    // Nettoyage
    cleanup
  };
};
